import { assertUnreachable } from '@sgme/fp';
import { publicationApi } from 'apiConnector/discover';
import { DISCOVER_DEFAULT_TECHNICAL_ERROR_KEY } from 'components/discover/shared';
import { useFetchArrayData } from 'hooks/useFetchData';
import { FC, useCallback, useContext } from 'react';
import type { Observable } from 'rxjs';

import { ErrorWithRetry } from '../../shared/ErrorWithRetry';
import { RegionContext } from '../contexts/RegionContext';
import type { TopTradeItem } from './model';
import { TopTrades } from './TopTrades';
import { TopTradesItem, TopTradesItemSkeleton } from './TopTradesItem';

export interface TopTradesContainerProps {
  fetch: (region: string) => Observable<TopTradeItem[]>;
}

export const TopTradesContainer: FC<TopTradesContainerProps> = ({ fetch }) => {
  const { region } = useContext(RegionContext);
  const fetchPublications = useCallback(() => fetch(region), [fetch, region]);

  const [status, items, retry] = useFetchArrayData(fetchPublications);
  switch (status) {
    case 'success':
      return (
        <TopTrades>
          {(items ?? []).map(({ id, title, content, documentLink, pictureLink, pictureUrl }) => (
            <TopTradesItem
              dimension="sm"
              key={id ?? title}
              title={title}
              content={content}
              documentLink={documentLink}
              pictureLink={pictureLink}
              pictureUrl={pictureUrl}
            />
          ))}
        </TopTrades>
      );
    case 'loading':
      return (
        <TopTrades>
          <TopTradesItemSkeleton lines={5} />
          <TopTradesItemSkeleton lines={6} />
        </TopTrades>
      );
    case 'error':
      return (
        <TopTrades>
          <ErrorWithRetry onRetry={retry} e2e="top-trades-error" msgKey={DISCOVER_DEFAULT_TECHNICAL_ERROR_KEY} />
        </TopTrades>
      );
    case 'unauthorized':
    case 'empty':
      return null;

    default:
      return assertUnreachable(status, 'Unhandled status');
  }
};

export const TopTradesConnected: FC = () => <TopTradesContainer fetch={publicationApi.fetchTopTrades} />;
